import Papa from "papaparse";
import { BaseFermentationParser, } from "./parser-interface";
/**
 * OGA parser implementing the FermentationTimeseriesParser interface
 */
export class OGAParser extends BaseFermentationParser {
    equipmentType = "OGA";
    supportedFormats = [".csv"];
    argsSchema = {
        isMicroOGA: {
            type: "boolean",
            required: false,
            description: "Is this data from a microOGA device? (affects column mapping)",
        },
        overrideTemperature: {
            type: "number",
            required: false,
            description: "Override gas temperature value (°C) - use if OGA doesn't provide reliable temperature",
        },
        overrideHumidity: {
            type: "number",
            required: false,
            description: "Override relative humidity value (%) - use if OGA doesn't provide humidity data",
        },
        startTimeOffset: {
            type: "number",
            required: false,
            description: "Time offset in hours if OGA start time differs from inoculation time",
        },
        interpolateZeros: {
            type: "boolean",
            required: false,
            description: "Replace zero values with linear interpolation (recommended)",
        },
    };
    rawData;
    headers;
    isMicroOGA;
    constructor() {
        super();
    }
    decodeFile(buffer) {
        // CSV files are typically UTF-8 text, no special decoding needed
        return buffer;
    }
    async validateFile(buffer) {
        try {
            const text = new TextDecoder("utf-8").decode(buffer);
            const errors = [];
            const warnings = [];
            // Parse CSV
            let parseResult;
            try {
                parseResult = Papa.parse(text, {
                    skipEmptyLines: true,
                    header: false,
                });
                if (parseResult.errors.length > 0) {
                    errors.push(`CSV parsing errors: ${parseResult.errors.map((e) => e.message).join(", ")}`);
                }
            }
            catch (parseError) {
                errors.push(`Failed to parse CSV: ${parseError}`);
                return { isValid: false, errors };
            }
            const records = parseResult.data;
            if (records.length < 2) {
                errors.push("CSV file must have at least a header row and one data row");
                return { isValid: false, errors };
            }
            const headers = records[0];
            // Check for microOGA format
            const isMicroOGA = headers.includes("elapsedTimeS") &&
                headers.includes("o2_conc") &&
                headers.includes("co2_conc");
            // Check for standard OGA format (no header in original format)
            const isStandardOGA = headers.length >= 15 && !headers.includes("elapsedTimeS");
            if (!isMicroOGA && !isStandardOGA) {
                errors.push("File does not appear to be a valid OGA format. Expected microOGA format with 'elapsedTimeS', 'o2_conc', 'co2_conc' columns or standard OGA format with 15+ columns.");
            }
            // Validate required columns for microOGA
            if (isMicroOGA) {
                const requiredColumns = [
                    "elapsedTimeS",
                    "o2_conc",
                    "co2_conc",
                    "flowTempC",
                ];
                const missingColumns = requiredColumns.filter((col) => !headers.includes(col));
                if (missingColumns.length > 0) {
                    errors.push(`Missing required microOGA columns: ${missingColumns.join(", ")}`);
                }
            }
            // Check data rows
            if (records.length < 10) {
                warnings.push("File has very few data points, results may be limited");
            }
            return {
                isValid: errors.length === 0,
                errors,
                warnings,
            };
        }
        catch (error) {
            return {
                isValid: false,
                errors: [`File validation failed: ${error}`],
            };
        }
    }
    async extractMetadata(buffer) {
        this.initializeData(buffer);
        // OGA files carry elapsed time only (no absolute timestamps), so the span
        // is the largest elapsed-time value across the data rows.
        let maxTimeS = null;
        for (let i = 1; i < this.rawData.length; i++) {
            const row = this.createRowObject(this.rawData[i]);
            const timeS = this.getTimeInSeconds(row);
            if (timeS === null)
                continue;
            if (maxTimeS === null || timeS > maxTimeS)
                maxTimeS = timeS;
        }
        return {
            equipmentType: this.equipmentType,
            startTime: undefined, // Will use fermentation start time
            durationSeconds: maxTimeS ?? undefined,
            tankNumbers: [0], // OGA data is typically for one fermentation setup
        };
    }
    async parseData(buffer, fermentations, _tankIdToFermIdMap, args, _additionTotals) {
        this.initializeData(buffer);
        const records = [];
        // Process each data row
        for (let i = 1; i < this.rawData.length; i++) {
            const row = this.createRowObject(this.rawData[i]);
            // Calculate timestamp
            const timeSeconds = this.getTimeInSeconds(row, args);
            if (timeSeconds === null)
                continue;
            // Apply time offset if specified
            const startTimeOffset = (args?.startTimeOffset || 0) * 3600; // Convert hours to seconds
            const timestamp_s = timeSeconds + startTimeOffset;
            // Create processed data object
            const processedData = this.processRowData(row, args);
            // Map attributes to standardized format
            const attributes = this.mapAttributes(processedData);
            records.push({
                timestamp_s,
                attributes,
            });
        }
        // Apply interpolation if enabled
        if (args?.interpolateZeros) {
            this.interpolateZeroValues(records);
        }
        // OGA parser assumes single fermentation like Applikon/Biostream
        const ferm = fermentations[0];
        return [
            {
                fermentationId: ferm.id,
                records,
            },
        ];
    }
    getAttributeMapping() {
        return {
            // Gas concentrations
            oxygen_concentration_pct: "offgas_o2_pv",
            co2_concentration_pct: "offgas_co2_pv",
            // Temperature and environmental
            gas_temperature_c: "offgas_temp_pv",
            relative_humidity_pct: "offgas_rh_pv",
            // Flow measurements
            gas_flow_lpm: "offgas_flow_pv",
            // Metabolic rates (if available)
            our_ml_min_kg: "our",
            cer_ml_min_kg: "cer",
            rq_ratio: "rq",
            // Cumulative values (if available)
            cumulative_o2_l_kg: "our_total",
            cumulative_co2_l_kg: "cer_total",
            // Inlet conditions
            inlet_o2_pct: "inlet_o2_pv",
            inlet_co2_ppm: "inlet_co2_pv",
        };
    }
    initializeData(buffer) {
        const text = new TextDecoder("utf-8").decode(buffer);
        const parseResult = Papa.parse(text, {
            skipEmptyLines: true,
            header: false,
        });
        this.rawData = parseResult.data;
        if (!this.rawData || this.rawData.length === 0) {
            throw new Error("No data found in OGA file");
        }
        this.headers = this.rawData[0];
        // Detect format
        this.isMicroOGA =
            this.headers.includes("elapsedTimeS") &&
                this.headers.includes("o2_conc") &&
                this.headers.includes("co2_conc");
    }
    createRowObject(row) {
        const obj = {};
        if (this.isMicroOGA) {
            // Use actual headers for microOGA
            for (let i = 0; i < this.headers.length && i < row.length; i++) {
                const value = row[i];
                obj[this.headers[i]] =
                    value === "" || value === null
                        ? null
                        : !isNaN(Number(value))
                            ? Number(value)
                            : value;
            }
        }
        else {
            // Standard OGA format - assign standard column names
            const standardColumns = [
                "Time_s",
                "column2",
                "index_nr",
                "O2_%",
                "CO2_%",
                "Airflow_sL/min",
                "OUR_ml/min/kg",
                "CER_ml/min/kg",
                "RQ",
                "Cumm_O2_L/kg",
                "Cumm_CO2_L/kg",
                "Cumm_flow_L/kg",
                "Gas_Temp_C",
                "rel_Humidity_%",
                "Inlet_O2",
                "Inlet_CO2_ppm",
            ];
            for (let i = 0; i < standardColumns.length && i < row.length; i++) {
                const value = row[i];
                obj[standardColumns[i]] =
                    value === "" || value === null
                        ? null
                        : !isNaN(Number(value))
                            ? Number(value)
                            : value;
            }
        }
        return obj;
    }
    getTimeInSeconds(row, args) {
        if (this.isMicroOGA) {
            const timeS = this.toNumber(row["elapsedTimeS"]);
            return timeS;
        }
        else {
            const timeS = this.toNumber(row["Time_s"]);
            return timeS;
        }
    }
    processRowData(row, args) {
        let processedData;
        if (this.isMicroOGA) {
            processedData = {
                oxygen_concentration_pct: this.toNumber(row["o2_conc"]),
                co2_concentration_pct: this.toNumber(row["co2_conc"]),
                gas_temperature_c: this.toNumber(row["flowTempC"]),
                gas_flow_lpm: this.toNumber(row["flowLPM"]),
                our_ml_min_kg: this.toNumber(row["our_val"]),
                cer_ml_min_kg: this.toNumber(row["cer_val"]),
                rq_ratio: this.toNumber(row["rq_ratio"]),
                cumulative_o2_l_kg: this.toNumber(row["total_o2"]),
                cumulative_co2_l_kg: this.toNumber(row["total_co2"]),
                inlet_o2_pct: this.toNumber(row["inletO2"]),
                inlet_co2_ppm: this.toNumber(row["inletCO2"]),
            };
        }
        else {
            processedData = {
                oxygen_concentration_pct: this.toNumber(row["O2_%"]),
                co2_concentration_pct: this.toNumber(row["CO2_%"]),
                gas_temperature_c: this.toNumber(row["Gas_Temp_C"]),
                relative_humidity_pct: this.toNumber(row["rel_Humidity_%"]),
                gas_flow_lpm: this.toNumber(row["Airflow_sL/min"]),
                our_ml_min_kg: this.toNumber(row["OUR_ml/min/kg"]),
                cer_ml_min_kg: this.toNumber(row["CER_ml/min/kg"]),
                rq_ratio: this.toNumber(row["RQ"]),
                cumulative_o2_l_kg: this.toNumber(row["Cumm_O2_L/kg"]),
                cumulative_co2_l_kg: this.toNumber(row["Cumm_CO2_L/kg"]),
                inlet_o2_pct: this.toNumber(row["Inlet_O2"]),
                inlet_co2_ppm: this.toNumber(row["Inlet_CO2_ppm"]),
            };
        }
        // Apply overrides if specified
        if (args?.overrideTemperature && args.overrideTemperature > 0) {
            processedData.gas_temperature_c = args.overrideTemperature;
        }
        if (args?.overrideHumidity && args.overrideHumidity > 0) {
            processedData.relative_humidity_pct = args.overrideHumidity;
        }
        return processedData;
    }
    interpolateZeroValues(records) {
        // Get all attribute names that might have zero values to interpolate
        // Registry names, not source columns: interpolation runs after
        // `mapAttributes`, on the standardised record.
        const attributesToInterpolate = [
            "offgas_o2_pv",
            "offgas_co2_pv",
            "offgas_temp_pv",
            "offgas_rh_pv",
        ];
        for (const attrName of attributesToInterpolate) {
            const values = records.map((r) => r.attributes[attrName]);
            // Replace zeros with null for interpolation
            const valuesWithNulls = values.map((v) => (v === 0 ? null : v));
            // Linear interpolation
            const interpolated = this.linearInterpolate(valuesWithNulls);
            // Apply interpolated values back to records
            for (let i = 0; i < records.length; i++) {
                if (interpolated[i] !== null) {
                    records[i].attributes[attrName] = interpolated[i];
                }
            }
        }
    }
    linearInterpolate(values) {
        const result = [...values];
        for (let i = 0; i < result.length; i++) {
            if (result[i] === null) {
                // Find previous valid value
                let prevIndex = i - 1;
                while (prevIndex >= 0 && result[prevIndex] === null) {
                    prevIndex--;
                }
                // Find next valid value
                let nextIndex = i + 1;
                while (nextIndex < result.length && result[nextIndex] === null) {
                    nextIndex++;
                }
                // Interpolate if we have both previous and next values
                if (prevIndex >= 0 && nextIndex < result.length) {
                    const prevValue = result[prevIndex];
                    const nextValue = result[nextIndex];
                    const ratio = (i - prevIndex) / (nextIndex - prevIndex);
                    result[i] = prevValue + (nextValue - prevValue) * ratio;
                }
                else if (prevIndex >= 0) {
                    // Forward fill
                    result[i] = result[prevIndex];
                }
                else if (nextIndex < result.length) {
                    // Backward fill
                    result[i] = result[nextIndex];
                }
            }
        }
        return result;
    }
}
//# sourceMappingURL=oga.js.map